/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactElement, useMemo } from "react";

// eslint-disable-next-line
import { css, jsx } from "@compiled/react";

import { Field } from "@atlaskit/form";
import Select, { type ValueType } from "@atlaskit/select";
import { N50, N500, R400 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

const fieldLabelStyles = css({
    color: token("color.text.subtle", N500),
    fontFamily: token("font.family.brand.body"),
    fontStyle: "normal",
    fontWeight: token("font.weight.medium"),
    fontSize: "14px",
    lineHeight: "15px"
});

const errorMessageStyles = css({
    color: token("color.text.danger", R400),
    fontFamily: token("font.family.brand.body"),
    fontWeight: token("font.weight.regular"),
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "0",
    marginBlockStart: token("space.050", "4px")
});

export type SiteNameDropdownOption = {
    label: string;
    value: string;
};

export type SiteNameDropdownProps = {
    readonly fieldName: string;
    readonly fieldLabel?: string;
    readonly renderPreview?: (value: string) => ReactElement;
    readonly sites: SiteNameDropdownOption[];
    readonly onChange?: (value: string) => void;
};

export const SiteNameDropdown = ({
    fieldName,
    fieldLabel,
    renderPreview,
    sites,
    onChange
}: SiteNameDropdownProps) => {
    const defaultValue = useMemo(() => {
        if (sites.length) {
            return sites[0];
        } else {
            return undefined;
        }
    }, [sites]);

    return (
        <Field<ValueType<SiteNameDropdownOption>>
            name={fieldName}
            label={<span css={fieldLabelStyles}>{fieldLabel}</span>}
            isRequired
            defaultValue={defaultValue}
        >
            {({ fieldProps: { id, ...rest }, error }) => {
                const urlPreview = rest.value
                    ? rest.value.value
                    : "your-company-name.atlassian.net";

                return (
                    <div>
                        <Select<SiteNameDropdownOption>
                            inputId={id}
                            options={sites}
                            styles={{
                                // https://staging.atlassian.design/components/select/code#Styles%20API
                                // https://react-select.com/styles
                                container: (baseStyles) => ({
                                    ...baseStyles,
                                    fontSize: 14,
                                    lineHeight: 1.4
                                }),
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    borderColor: state.isFocused
                                        ? baseStyles.borderColor
                                        : `${token(
                                              "color.border",
                                              N50
                                          )} !important`
                                })
                            }}
                            {...rest}
                            onChange={(newValue) => {
                                rest.onChange?.(newValue);
                                if (newValue) {
                                    onChange?.(newValue.value);
                                }
                            }}
                        />

                        {error && <span css={errorMessageStyles}>{error}</span>}
                        {renderPreview && renderPreview(urlPreview)}
                    </div>
                );
            }}
        </Field>
    );
};
